/**
 * Lifts the Gorgias chat widget out of the way of checkout buttons.
 *
 * The widget is an app embed, so it can only be repositioned from the theme.
 * Its resting spot in the bottom right corner covers the checkout button on the
 * cart page and the sticky add to cart panel, which makes those buttons
 * impossible to tap. Paired with chat-widget-position.css, which hides the
 * widget while a drawer or modal is open.
 */
(() => {
  const WIDGET_SELECTOR = '#gorgias-chat-container #chat-button, #gorgias-chat-container #mount-floating-engagement-widget';
  const BUTTON_SELECTOR = [
    '[name="checkout"]',
    '.shopify-payment-button',
    '.additional-checkout-buttons',
    '.sticky-atc-panel'
  ].join(',');
  const GAP = 12;
  const MAX_LIFT = 200;

  let lift = 0;
  let frame = null;

  const isRendered = (el) => {
    if (getComputedStyle(el).display === 'none') return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0;
  };

  const isVisible = (el) => {
    const styles = getComputedStyle(el);
    if (styles.visibility === 'hidden' || styles.opacity === '0') return false;
    return isRendered(el);
  };

  const overlaps = (a, b) => a.left < b.right + GAP && a.right > b.left - GAP
    && a.top < b.bottom + GAP && a.bottom > b.top - GAP;

  /**
   * @returns {number} Pixels the widget needs to move up to clear every
   *   checkout button currently on screen.
   */
  function requiredLift() {
    const widgets = [];

    // Widgets are measured even while faded out. The lift is a transform, so it
    // applies just as well to a launcher mid fade-in after a drawer closes, and
    // skipping it there would clear a correct lift.
    document.querySelectorAll(WIDGET_SELECTOR).forEach((el) => {
      if (!isRendered(el)) return;
      const rect = el.getBoundingClientRect();
      // Measure from the resting position rather than the lifted one.
      widgets.push(new DOMRect(rect.left, rect.top + lift, rect.width, rect.height));
    });

    if (!widgets.length) return 0;

    let required = 0;

    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      if (!isVisible(button)) return;
      const buttonRect = button.getBoundingClientRect();

      widgets.forEach((widgetRect) => {
        if (!overlaps(widgetRect, buttonRect)) return;
        required = Math.max(required, widgetRect.bottom - buttonRect.top + GAP);
      });
    });

    return Math.min(Math.round(required), MAX_LIFT);
  }

  function update() {
    frame = null;
    if (document.body.classList.contains('fixed')) return;

    const next = requiredLift();
    if (next === lift) return;

    lift = next;
    document.documentElement.style.setProperty('--chat-widget-lift', `${lift}px`);
  }

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(update);
  }

  function watch(container) {
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('on:cart:add', schedule);
    document.addEventListener('on:line-item:change', schedule);
    // Drawers and modals toggle .fixed on the body.
    new MutationObserver(schedule).observe(document.body, {
      attributeFilter: ['class'],
      attributes: true
    });

    // Gorgias mounts its launcher into the container well after the container
    // exists, then reveals and resizes it by writing inline styles. None of that
    // fires a scroll or resize, so watch the container itself.
    const sizes = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;
    const observed = new WeakSet();
    const onContainerChange = () => {
      if (sizes) {
        container.querySelectorAll(WIDGET_SELECTOR).forEach((el) => {
          if (observed.has(el)) return;
          observed.add(el);
          sizes.observe(el);
        });
      }
      schedule();
    };
    new MutationObserver(onContainerChange).observe(container, {
      attributeFilter: ['class', 'style'],
      attributes: true,
      childList: true,
      subtree: true
    });
    onContainerChange();
  }

  const container = document.querySelector('#gorgias-chat-container');
  if (container) {
    watch(container);
  } else {
    const observer = new MutationObserver(() => {
      const found = document.querySelector('#gorgias-chat-container');
      if (!found) return;
      observer.disconnect();
      watch(found);
    });
    observer.observe(document.body, { childList: true });
  }
})();
